/**
 * Created by mrocha on 26/10/16.
 */

import Immutable    from 'immutable';
import restService  from 'RestService.js';
import constants    from 'Constants.js';
import undoService from 'UndoService';
import listService from 'ListService';
import eventBus  from 'EventBus.js';
import textConstants        from 'TextConstants';
import NotificationService from 'NotificationService.js';
import ReportLeftPanelView from 'dashboard/reports/ReportLeftPanelView';
import zLib                 from 'util/ZLib';
import formulaService from './xml/FormulaService';
import util from './util/Util';
import xjService from './xml/XJService';

//===========================================================================================
// This service creates a new form on the host.
//===========================================================================================
class ReportAddService {

    constructor() {

        // Prototype report passed to /Report/Add
        // Compressed to binary and then base 64 encoded. Don't introduce whitespace, else it breaks.
        this._reportXML = `H4sIAAAAAAAA/3jajVdbj9soFP4rFs/RNsm0s600HsnJXNqqo7kknUr7UhFDHLYELMCTyTzsb98DGAczjhRegvHhXL7vO+BcPNFaKpMxkiOUCbylORIN5yirGrumNve336uiUPtdUXxXs6KYz1ePy8fbN5mjjMjymSrNpMjRGGUv3fyvCbq8eFCypsowqrOSCkPVV6nYmxQG8xwZ1VCUGWZ4F3GtIPxccqlydH5eluu1z2guG7vbhahx1Xt2exbsDZxMzlG2Y8RscvR53Jper9e0NBBACohWKUa+UlZtYGUKFpt2fm7NSc1y9PcUJgxX4FXfxtafUKYhyoOimsJz2Wgjt97jLx/TOmS6KEuqNVvZqnyJsjGakVAXdQNljWDgpmavNBSeozXmmvq8l7JelEryDii9wUTuwpPb8AQwkLlUAlAPL5i+a7hhP7CoOn9M/xQOZxKs4gpD9lCgJ+nZUlYeKFKGB1cfLi8WBhuq21+nGsz5b2zpe8G8sVxKtcXc2n4Ixverf4GFbuK2rRY/Hs9AWD9vrovi7p+7opjhX4/L56vXMQjL7GtwVSnZ1EGVDwBLlsjK4NW1wKuotFpRLjH5tgXrsLbBeul11iLi0GgJuXEDZdgRd0V1aRvBWdyAVHMEoiZY4FE2Gff1xenaOA32ZLTFGkCcG8Wtn7d7RVqlGoWFrrGiotyDWEEYK84qbKTa30Fkly/KVtJvmIwyLTkjIbEF49RmE1cww+UfC5FVgatlPP7yxdbic7BUBIetQelGuz3uhFXq6j+rwlnlnlr7AtKF3rZlxxsc1Auzt/h6BYG9lHzJaoekrH351sWSvhq7WGNCmJVoDxdzKKgrNIlTaGi/b6TLzy06rXlpdRMQmxVM+3OC4noisw7b7aBhZdL5oPAJXWNovqD8btdyQ7d+R1+8nIk/FpAW8TMY9sRzop/FrI3dQFbtPHACALkBCDXGSGEdXcmdSLb4ly07daM3YWlWRdbhUOJYVSALbLnx4n+Qdc2EHmXT8QikxMkoa/s7MxAxMZpMw+vIzCZtfVJ1grUrPgIl1OFBGS69Ww5laHDFE2TPzmKk7l+GwfV5zqpbBRiAIJN4noCj1DzJnVueJDvC+vSQIhN1Y3qeQpIxBwkQ9vWWEtZsj7/vlZGk74IO4BK1UriKe2FS3j6nUgg8JLCFYqOcBqKb92x7fyfIxTF9JMuPB3MGXxysjISR2o7fu3ZgnZBCxOTh1IZ+gWiUw0HETiqk1ffycBYejvqD6OCs7geAo8ReIIkGusCJ1CduvKcrMgmUHVoloabnO2Gyy8d/j33sIg3pPKZuQMce1mMNHzIfOMOCyUBRXe5HG9weyafIrnM0dBY4Lge8DPXME6TRwNfbNBx97l6vBnNOco3uCSc8+4nrZBHd3Kl1r8ntPeXupsuLK2zwQjYKvjTsqv9PcPk/4CQeiQerRbgcDAAA`;
    }

    get defaultReport() { 
        this.setDefaultFont();
        return this._reportXML; 
    }

    decompressReport(){
        let decompressedForm = zLib.decompressForm( this._reportXML );
        decompressedForm = util.minifyXMLString(decompressedForm);

        const
            formDoc = formulaService.preprocess(decompressedForm),
            formObj = xjService.xmlDocToJs(formDoc);
        
        return formObj;
    }

    compressReport(formObj){
        
        const reportXML = xjService.xmlToString( formulaService.postProcess( formObj ) );
        this._reportXML = zLib.compressForm(reportXML);
    }

    //PF-9500 set default font from account details in new forms
    setDefaultFont(){

        const defaultFont = listService.accountSettings.DefaultFontSelected || "verdana";
        const form = this.decompressReport();
        const themeProps = form.Report.Theme.Properties;
        const themeFonts = Object.keys(themeProps).filter((key) => key.toLowerCase().includes("font"));
        
        _.each(themeFonts, (font) => {
            let f = themeProps[font].trim().split(",");
            f[0] = defaultFont;
            themeProps[font] = f.join(",");
        });

        this.compressReport(form);
    }


    //===========================================================================================
    // This method creates a new report on the host.
    //
    // The name of the report will be "New Report", unless a report of that name exists for this user.
    // In that case the name is "New Report (n)" where 'n' is the highest member of an increasing
    // sequence of 'n'.
    //
    // Return  object: name/value pairs for keys: ReportId, Name, GroupId, GroupName
    //
    //===========================================================================================

    newReport(groupId, selectedGroupType, selectedNodeOnTree, selectedNodeTarget, reportXML = this.defaultReport,
            reportName = constants.DEFAULT_REPORT_NAME, isPrivate = false) {
        // If there is no group id, or public folder or shared folder are selected,
        // then use the default group id
        let _groupId = groupId;
        if (!groupId || groupId === '3' || groupId === '4') {
            _groupId= constants.DEFAULT_GROUP_ID;
        }
        // Report name becomes "New Report" or "New Report (n)"
        return new Promise((resolve, reject) => {

            // Build and carry out request
            restService.addReport( reportXML, reportName, _groupId,isPrivate).then(data => {
                // Set success banner message for use by caller.
                const goodMsg = `Report "${data.name}", ID "${data.reportId}" created.`;
                const itemId = `${data.GroupId}_${data.reportId}`;
                const result = {
                    msg: goodMsg,
                    reportId: data.reportId,
                    name: data.name,
                    groupType: data.GroupId,
                    itemId: itemId
                };
                undoService.add({
                    undo: () => {
                        this.deleteReport(data.reportId,data.name);
                    },
                    redo: ()=>{
                        this.unDeleteReport(data.reportId, _groupId, data.GroupId,result.itemId,selectedGroupType,selectedNodeOnTree,selectedNodeTarget);
                    }
                }, textConstants.UNDO_NEW_REPORT);
                resolve(result);
            }, err => {
                console.log(err);
                if (err && typeof err === 'string') {
                    reject(err);
                } else if (err && typeof err === 'object' && err.responseText) {
                    const msg = JSON.parse(err.responseText);
                    reject(`${msg.Message}. ${msg.ExceptionMessage}.`);
                } else {
                    reject(err);
                }
            });
        });
    }

    deleteReport(reportId,name) {
        return restService.deleteReport(reportId).then((data) => {
            if(listService.activeDashboardTab === 'reports') {
                eventBus.trigger('deletingReport', reportId,false,name);
            }
            else {
                NotificationService.success(`Report ${name} was deleted. Clicking redo restores it.`);
            }
            return (data);
        }).catch((error) => {
            var msg = error || 'Unknown Error';
            NotificationService.error(msg);
            return(error);
        });
    }

    unDeleteReport(reportId, groupId, groupType,itemId,selectedGroupType,selectedNodeOnTree,selectedNodeTarget ){
        return restService.unDeleteReport(reportId).then(data => {
            if(listService.activeDashboardTab === 'reports') {
                ReportLeftPanelView.expandTreeReportsPrivate();

                eventBus.trigger(constants.EV_UPDATE_REPORT_DETAILS, reportId, selectedGroupType, itemId);
                eventBus.trigger('unDeletingReport', {
                    msg: `Report "${data.Name}" restored`,
                    reportId,
                    name: data.Name
                }, selectedNodeOnTree, selectedNodeTarget);
            }
            else{
                NotificationService.success(`Report ${data.Name} restored.`);
            }
            return data;
        }, error => {
            return error;
        });

    }

}

const reportAddService = new ReportAddService();
export default reportAddService;
